import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import TreeIcon from "../../assets/img/icons/tree.svg";
import CloseIcon from "../../assets/img/icons/close.svg";
import DecisionTree from "../DecisionTree";

const DecisionTreeModal = ({ isOpen, onClose, decisionData, directionsData = [], allDecisions = [], incomingRelations = [], decisionReceivers = [] }) => {
	// Handle ESC key to close modal
	useEffect(() => {
		const handleEscKey = (event) => {
			if (event.keyCode === 27) {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleEscKey, false);
		}

		return () => {
			document.removeEventListener("keydown", handleEscKey, false);
		};
	}, [isOpen, onClose]);

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		// Cleanup function to restore scroll when component unmounts
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isOpen]);

	if (!isOpen) return null;

	const modalContent = (
		<div 
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				width: "100vw",
				height: "100vh",
				backgroundColor: "rgba(15, 23, 42, 0.75)",
				backdropFilter: "blur(8px)",
				zIndex: 99999,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: 0,
				margin: 0,
				animation: "fadeIn 0.2s ease-out"
			}}
			onClick={onClose}
		>
			<style>
				{`
					@keyframes fadeIn {
						from { opacity: 0; }
						to { opacity: 1; }
					}
					@keyframes slideUp {
						from { 
							opacity: 0;
							transform: translateY(30px) scale(0.95);
						}
						to { 
							opacity: 1;
							transform: translateY(0) scale(1);
						}
					}
					@keyframes pulse {
						0%, 100% { opacity: 1; }
						50% { opacity: 0.5; }
					}
				`}
			</style>
			<div 
				style={{
					position: "relative",
					width: "60vw",
					height: "85vh",
					backgroundColor: "white",
					borderRadius: "16px",
					display: "flex",
					flexDirection: "column",
					boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
					animation: "slideUp 0.3s ease-out",
					overflow: "hidden",
					margin: 0,
					padding: 0
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header */}
				<div style={{ 
					display: "flex", 
					justifyContent: "space-between", 
					alignItems: "flex-start", 
					padding: "32px 40px 24px 40px",
					borderBottom: "1px solid #e2e8f0",
					backgroundColor: "white",
					borderTopLeftRadius: "16px",
					borderTopRightRadius: "16px",
					width: "100%",
					boxSizing: "border-box"
				}}>
					<div style={{ flex: 1 }}>
						<h2 style={{
							margin: "0 0 16px 0",
							fontSize: "32px",
							fontWeight: "800",
							color: "#0f172a",
							letterSpacing: "-0.025em",
							lineHeight: "1.1"
						}}>
							{decisionData.name}
						</h2>
						<div style={{
							display: "flex",
							flexWrap: "wrap",
							gap: "16px",
							marginBottom: "8px"
						}}>
							<div style={{
								display: "flex",
								flexDirection: "column",
								gap: "4px"
							}}>
								<span style={{
									fontSize: "12px",
									fontWeight: "600",
									color: "#64748b",
									textTransform: "uppercase",
									letterSpacing: "0.05em"
								}}>
									Unit
								</span>
								<span style={{
									fontSize: "14px",
									fontWeight: "500",
									color: "#1e293b"
								}}>
									{decisionData.unit?.name || "N/A"}
								</span>
							</div>
							<div style={{
								display: "flex",
								flexDirection: "column",
								gap: "4px"
							}}>
								<span style={{
									fontSize: "12px",
									fontWeight: "600",
									color: "#64748b",
									textTransform: "uppercase",
									letterSpacing: "0.05em"
								}}>
									Label
								</span>
								<span style={{
									fontSize: "14px",
									fontWeight: "500",
									color: "#1e293b"
								}}>
									{decisionData.label?.name || "N/A"}
								</span>
							</div>
							<div style={{
								display: "flex",
								flexDirection: "column",
								gap: "4px"
							}}>
								<span style={{
									fontSize: "12px",
									fontWeight: "600",
									color: "#64748b",
									textTransform: "uppercase",
									letterSpacing: "0.05em"
								}}>
									Category
								</span>
								<span style={{
									fontSize: "14px",
									fontWeight: "500",
									color: "#1e293b"
								}}>
									{decisionData.category?.name || "N/A"}
								</span>
							</div>
							<div style={{
								display: "flex",
								flexDirection: "column",
								gap: "4px"
							}}>
								<span style={{
									fontSize: "12px",
									fontWeight: "600",
									color: "#64748b",
									textTransform: "uppercase",
									letterSpacing: "0.05em"
								}}>
									Decision ID
								</span>
								<span style={{
									fontSize: "14px",
									fontWeight: "500",
									color: "#1e293b"
								}}>
									{decisionData.id}
								</span>
							</div>
						</div>
					</div>
					<button 
						onClick={onClose}
						style={{
							border: "none",
							background: "transparent",
							outline: "none",
							cursor: "pointer",
							width: "16px",
							height: "16px",
							padding: 0,
							marginLeft: "24px",
							flexShrink: 0
						}}
						title="Close"
					>
						<img 
							src={CloseIcon} 
							alt="Close" 
							style={{ 
								width: "16px", 
								height: "16px"
							}} 
						/>
					</button>
				</div>
				
				{/* Content Area */}
				<div style={{ 
					flex: 1, 
					backgroundColor: "#fefefe",
					position: "relative",
					minHeight: "0"
				}}>
					<DecisionTree 
						decisionData={decisionData}
						directionsData={directionsData}
						allDecisions={allDecisions}
						incomingRelations={incomingRelations}
						decisionReceivers={decisionReceivers}
					/>
				</div>
				
				{/* Footer */}
				<div style={{
					padding: "16px 40px",
					borderTop: "1px solid #e2e8f0",
					backgroundColor: "#f8fafc",
					borderBottomLeftRadius: "16px",
					borderBottomRightRadius: "16px",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					minHeight: "52px"
				}}>
					<div style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
						color: "#64748b",
						fontSize: "13px",
						fontWeight: "500"
					}}>
						<span>Navigate: Click + Drag</span>
						<span style={{padding: "0 8px"}}>•</span>
						<span>Zoom: Mouse Wheel</span>
					</div>
					<div style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
						color: "#64748b",
						fontSize: "13px"
					}}>
						<span>Press ESC to close</span>
					</div>
				</div>
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
};

DecisionTreeModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	decisionData: PropTypes.object.isRequired,
	directionsData: PropTypes.array,
	allDecisions: PropTypes.array,
	incomingRelations: PropTypes.array,
	decisionReceivers: PropTypes.array,
};

export default DecisionTreeModal;
